import React, { useEffect, useState } from 'react';

import {
  Breakpoints,
  Clock,
  CTA,
  CustomImage,
  DisableFocusWrapper,
  SupDisclaimer,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
  useIsMobile,
} from '@vw-marketing/us-components';

import { BadgeIcon } from '../../../../components/icons/BadgeIcon';
import {
  ImageOfferData,
  OfferInformation,
  OffersModel,
} from '../../../../hooks-store/typings/incentive-store';
import { useFeatureAppConfig } from '../../../../hooks/use-feature-app-config';
import { useFeatureAppContent } from '../../../../hooks/use-feature-app-content';
import { useTranslations } from '../../../../hooks/use-translations';
import { AppConstants } from '../../../../utils/app-constants';
import {
  formatSalesEventDate,
  separateThousand,
  transformIrisUrl,
} from '../../../../utils/general';
import {
  CardContentWrapper,
  StyledButtonContainer,
  StyledLinkButtonContainer,
  StyledLogoWrapper,
  StyledModelContainer,
  StyledModelMsrpWrapper,
  StyledModelTrimContainer,
  StyledMsrpContainer,
  StyledOfferDetailsWrapper,
  StyledOfferHightlight,
  StyledOfferMaterialInfo,
  StyledOfferMaterialInfoWrapper,
  StyledOfferPaymentDetailsContainer,
  StyledOfferPriceContainer,
  StyledOffersCTAWrapper,
  StyledOfferTermContainer,
  StyledOfferTermsAndHighlightContainer,
  StyledOfferTimeContainer,
  StyledOfferTitleContainer,
  StyledPaymentTermsContainer,
  StyledTimeExpiry,
} from './styled';
interface CardContentProps {
  goToInventory: (value: OfferInformation) => void;
  readonly modelMsrp?: string;
  readonly offer: OfferInformation;
  setFormData: (value: OffersModel) => void;
}

const carTilePriceTextStyle = {
  tag: TextTag.span,
  appearance: TextAppearance.headline200,
  style: {
    [Breakpoints.default]: {
      fontWeight: TextWeight.bold,
      color: TextColor.inherit,
      fontSize: TextSize.TextSize20,
      lineHeight: TextLineHeight.TextLineHeight24,
    },
    [Breakpoints.b960]: {
      fontWeight: TextWeight.bold,
      color: TextColor.inherit,
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight20,
    },
    [Breakpoints.b1600]: {
      fontWeight: TextWeight.bold,
      color: TextColor.inherit,
      fontSize: TextSize.TextSize24,
      lineHeight: TextLineHeight.TextLineHeight28,
    },
  },
};

export const CardContent = ({
  goToInventory,
  modelMsrp,
  offer,
  setFormData,
}: CardContentProps): React.JSX.Element => {
  const [imageMap, setImageMap] = useState<ImageOfferData>(offer.imageData);
  const [specialOffer, setSpecialOffer] = useState<OffersModel>(
    offer.specialOffer! || {},
  );

  const isTablet: boolean = useIsMobile(Breakpoints.b1600);
  const isDesktop: boolean = useIsMobile(Breakpoints.b1280);

  const { faHostServerUrl } = useFeatureAppConfig();
  const { sales_event_logo_url_dark = '' } = useFeatureAppContent();
  const translations = useTranslations().salesEvent;

  useEffect(() => {
    if (offer.imageData) {
      setImageMap(offer.imageData);
    }
  }, [offer.imageData]);

  useEffect(() => {
    if (imageMap) {
      const mediaImageUrl =
        transformIrisUrl(
          imageMap?.modelImage,
          AppConstants.IrisImgPositionE13,
          '960',
          '432',
          imageMap?.modelYear,
          offer?.modelKey,
          imageMap?.isNewImage,
        ) ?? faHostServerUrl + AppConstants.Ngw6Logo960x432;

      setSpecialOffer(prevOffer => ({
        ...prevOffer,
        mediaImageUrl,
        modelCustomCta: offer.modelCustomCta,
        modelDisplayName: offer?.modelDisplayName,
        modelKey: offer?.modelKey,
      }));
    }
  }, [imageMap, offer?.modelKey]);

  const showMsrp: boolean =
    specialOffer?.dealOfferTypes[0].toLowerCase() === 'apr' ||
    specialOffer?.dealOfferTypes[0].toLowerCase() === 'other';

  const showOfferType = specialOffer.customContent?.showOfferType === 'yes';

  return (
    <CardContentWrapper>
      {sales_event_logo_url_dark && !isDesktop && (
        <StyledLogoWrapper>
          <CustomImage
            src={sales_event_logo_url_dark}
            alt="Sales_Event_Logo"
            srcPlaceholder={AppConstants.PlaceHolder480x216 as string}
          />
        </StyledLogoWrapper>
      )}
      <StyledModelMsrpWrapper>
        <StyledModelContainer>
          <TextCustom
            appearance={TextAppearance.headline200}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight20,
              },
              [Breakpoints.b1600]: {
                fontSize: TextSize.TextSize20,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
            }}
          >
            {specialOffer?.dealCarYear[0] || ''}
          </TextCustom>
          <StyledModelTrimContainer>
            <TextCustom
              appearance={TextAppearance.headline200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight20,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
              }}
            >
              {offer.modelKey === AppConstants.AllOfferModelKey
                ? offer.modelDisplayName
                : offer.modelDisplayName || ''}
            </TextCustom>
            <TextCustom
              appearance={TextAppearance.headline200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight20,
                },
                [Breakpoints.b1920]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
              }}
            >
              {specialOffer?.dealCarTrimName[0] || ''}
            </TextCustom>
          </StyledModelTrimContainer>
        </StyledModelContainer>
        {showMsrp && (
          <StyledMsrpContainer>
            <TextCustom
              appearance={TextAppearance.label100}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight16,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight20,
                },
              }}
            >
              {translations.cardContent.msrpStartingLabel}
            </TextCustom>
            <TextCustom
              appearance={TextAppearance.headline200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight20,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
              }}
            >
              ${separateThousand(modelMsrp ?? '0')}{' '}
              <TextCustom {...carTilePriceTextStyle}>
                {/*@ts-ignore */}
                <SupDisclaimer>1</SupDisclaimer>
              </TextCustom>
            </TextCustom>
          </StyledMsrpContainer>
        )}
      </StyledModelMsrpWrapper>
      <StyledOfferDetailsWrapper>
        <StyledOfferTermsAndHighlightContainer>
          <StyledPaymentTermsContainer>
            {specialOffer?.dealOfferTypes[0].toLowerCase() === 'other' ? (
              <StyledOfferTitleContainer>
                <TextCustom
                  appearance={TextAppearance.headline200}
                  tag={TextTag.p}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize20,
                      fontWeight: TextWeight.bold,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize24,
                      fontWeight: TextWeight.bold,
                      lineHeight: TextLineHeight.TextLineHeight28,
                    },
                  }}
                >
                  {specialOffer?.dealTitle}
                </TextCustom>
              </StyledOfferTitleContainer>
            ) : (
              <>
                <StyledOfferPaymentDetailsContainer>
                  {showOfferType && (
                    <TextCustom
                      appearance={TextAppearance.label150}
                      tag={TextTag.p}
                      style={{
                        [Breakpoints.default]: {
                          fontSize: TextSize.TextSize14,
                          fontWeight: TextWeight.light,
                          lineHeight: TextLineHeight.TextLineHeight16,
                        },
                        [Breakpoints.b1600]: {
                          fontSize: TextSize.TextSize16,
                          fontWeight: TextWeight.light,
                          lineHeight: TextLineHeight.TextLineHeight20,
                        },
                      }}
                    >
                      {specialOffer?.dealOfferTypes[0].toLowerCase() === 'apr'
                        ? translations.cardContent.aprLabel
                        : translations.cardContent.leasePaymentLabel}
                    </TextCustom>
                  )}
                  <StyledOfferPriceContainer>
                    {showOfferType &&
                      specialOffer?.dealOfferTypes[0].toLowerCase() ===
                        'lease' && (
                        <TextCustom
                          appearance={TextAppearance.copy200}
                          tag={TextTag.p}
                          style={{
                            [Breakpoints.default]: {
                              fontSize: TextSize.TextSize20,
                              fontWeight: TextWeight.bold,
                              lineHeight: TextLineHeight.TextLineHeight32,
                            },
                            [Breakpoints.b1600]: {
                              fontSize: TextSize.TextSize24,
                              fontWeight: TextWeight.bold,
                              lineHeight: TextLineHeight.TextLineHeight36,
                            },
                          }}
                        >
                          $
                        </TextCustom>
                      )}
                    <TextCustom
                      appearance={TextAppearance.headline300}
                      tag={TextTag.p}
                      style={{
                        [Breakpoints.default]: {
                          fontSize: TextSize.TextSize32,
                          fontWeight: TextWeight.bold,
                          lineHeight: TextLineHeight.TextLineHeight36,
                        },
                        [Breakpoints.b1600]: {
                          fontSize: TextSize.TextSize48,
                          fontWeight: TextWeight.bold,
                          lineHeight: TextLineHeight.TextLineHeight56,
                        },
                      }}
                    >
                      {specialOffer?.dealOfferTypes[0].toLowerCase() === 'lease'
                        ? separateThousand(specialOffer?.dealPricePerMonth || 0)
                        : specialOffer?.customContent?.aprValue ?? 0}
                    </TextCustom>
                    {showOfferType &&
                      specialOffer?.dealOfferTypes[0].toLowerCase() ===
                        'lease' && (
                        <div
                          style={{
                            alignSelf: 'end',
                            paddingBottom: isTablet ? 2 : 5,
                          }}
                        >
                          <TextCustom
                            appearance={TextAppearance.copy200}
                            tag={TextTag.p}
                            style={{
                              [Breakpoints.default]: {
                                fontSize: TextSize.TextSize16,
                                fontWeight: TextWeight.regular,
                                lineHeight: TextLineHeight.TextLineHeight24,
                              },
                              [Breakpoints.b1600]: {
                                fontSize: TextSize.TextSize20,
                                fontWeight: TextWeight.regular,
                                lineHeight: TextLineHeight.TextLineHeight32,
                              },
                            }}
                          >
                            {translations.cardContent.monthlyLabel}
                          </TextCustom>
                        </div>
                      )}
                  </StyledOfferPriceContainer>
                </StyledOfferPaymentDetailsContainer>
                {showOfferType && (
                  <StyledOfferTermContainer>
                    <TextCustom
                      appearance={TextAppearance.label150}
                      tag={TextTag.p}
                      style={{
                        [Breakpoints.default]: {
                          fontSize: TextSize.TextSize14,
                          fontWeight: TextWeight.regular,
                          lineHeight: TextLineHeight.TextLineHeight16,
                        },
                        [Breakpoints.b1600]: {
                          fontSize: TextSize.TextSize16,
                          fontWeight: TextWeight.regular,
                          lineHeight: TextLineHeight.TextLineHeight20,
                        },
                      }}
                    >
                      {translations.cardContent.termLabel}
                    </TextCustom>
                    <StyledOfferTimeContainer>
                      <TextCustom
                        appearance={TextAppearance.headline300}
                        tag={TextTag.p}
                        style={{
                          [Breakpoints.default]: {
                            fontSize: TextSize.TextSize32,
                            fontWeight: TextWeight.bold,
                            lineHeight: TextLineHeight.TextLineHeight36,
                          },
                          [Breakpoints.b1600]: {
                            fontSize: TextSize.TextSize48,
                            fontWeight: TextWeight.bold,
                            lineHeight: TextLineHeight.TextLineHeight56,
                          },
                        }}
                      >
                        {specialOffer?.dealOfferTypes[0].toLowerCase() === 'apr'
                          ? specialOffer?.customContent?.financeTermLength ?? ''
                          : specialOffer?.customContent?.leaseTermLength ?? 0}
                      </TextCustom>
                      <div
                        style={{
                          alignSelf: 'end',
                          paddingBottom: isTablet ? 2 : 5,
                        }}
                      >
                        <TextCustom
                          appearance={TextAppearance.copy200}
                          tag={TextTag.p}
                          style={{
                            [Breakpoints.default]: {
                              fontSize: TextSize.TextSize16,
                              fontWeight: TextWeight.regular,
                              lineHeight: TextLineHeight.TextLineHeight24,
                            },
                            [Breakpoints.b1600]: {
                              fontSize: TextSize.TextSize20,
                              fontWeight: TextWeight.regular,
                              lineHeight: TextLineHeight.TextLineHeight32,
                            },
                          }}
                        >
                          {translations.cardContent.monthsLabel}
                        </TextCustom>
                      </div>
                    </StyledOfferTimeContainer>
                  </StyledOfferTermContainer>
                )}
              </>
            )}
          </StyledPaymentTermsContainer>
          {specialOffer?.customContent?.offerHighlight && (
            <StyledOfferHightlight>
              <BadgeIcon />
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize20,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight32,
                  },
                }}
              >
                {specialOffer.customContent.offerHighlight}
              </TextCustom>
            </StyledOfferHightlight>
          )}   
        </StyledOfferTermsAndHighlightContainer>
        <StyledOfferMaterialInfoWrapper>
          <StyledOfferMaterialInfo>
            <TextCustom
              appearance={TextAppearance.copy200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight32,
                },
              }}
            >
              {specialOffer?.dealSubtitle || ''}
            </TextCustom>
          </StyledOfferMaterialInfo>
          <StyledTimeExpiry>
            <Clock />
            <TextCustom
              appearance={TextAppearance.copy200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight32,
                },
              }}
            >
              {translations.cardContent.offerEndsLabel}{' '}
              {formatSalesEventDate(specialOffer?.dealEndDate || '')}
            </TextCustom>
          </StyledTimeExpiry>
        </StyledOfferMaterialInfoWrapper>
      </StyledOfferDetailsWrapper>
      <StyledOffersCTAWrapper>
        <StyledButtonContainer>
          <CTA
            aria-label="View offer details CTA"
            emphasis="offer"
            onClick={() => setFormData(specialOffer)}
            tag="button"
          >
            <TextCustom
              appearance={TextAppearance.copy200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight20,
                },
              }}
            >
              {translations.cardContent.viewOfferDetailsCTALabel}
            </TextCustom>
          </CTA>
        </StyledButtonContainer>
        <StyledLinkButtonContainer>
          <DisableFocusWrapper>
            <CTA
              tag="a"
              emphasis="tertiary"
              aria-label="Go to inventory CTA"
              onClick={() => goToInventory(offer)}
              target="_blank"
              stretchContent
            >
              <TextCustom
                appearance={TextAppearance.label100}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight20,
                  },
                }}
              >
                {translations.cardContent.viewInventoryCTALabel}
              </TextCustom>
            </CTA>
          </DisableFocusWrapper>
        </StyledLinkButtonContainer>
      </StyledOffersCTAWrapper>
    </CardContentWrapper>
  );
};
